// ═══════════════════════════════════════════════════════════════
// EMAIL TEMPLATES — default subject + body text for Log Contact
//
// Templates are keyed by TEMPLATE_KEYS. Defaults below ship with
// the app; chapters can override subject/body in Settings →
// Email Templates (stored as JSON in app_settings.email_templates).
//
// Token syntax: {tokenName}. See utils/receiptTokens.js —
// buildEmailContext for the full vocabulary.
// ═══════════════════════════════════════════════════════════════

export const TEMPLATE_KEYS = {
  RENEWAL_ASK: "renewal_ask",
  LYBNT: "lybnt",
  SYBNT: "sybnt",
  SECOND_GIFT: "second_gift",
  THANK_YOU: "thank_you",
};

export const EMAIL_TEMPLATE_DEFINITIONS = [
  {
    key: TEMPLATE_KEYS.RENEWAL_ASK,
    label: "Renewal Ask",
    description: "Donors coming up on their annual renewal",
    defaultPurpose: ["gift_renewal"],
  },
  {
    key: TEMPLATE_KEYS.LYBNT,
    label: "LYBNT",
    description: "Gave last year but not this — re-engage before lapse",
    defaultPurpose: ["gift_renewal"],
  },
  {
    key: TEMPLATE_KEYS.SYBNT,
    label: "SYBNT",
    description: "Gave in a prior year but not this — warm reactivation",
    defaultPurpose: ["gift_renewal"],
  },
  {
    key: TEMPLATE_KEYS.SECOND_GIFT,
    label: "2nd Gift Ask",
    description: "One-time donor — the critical second-gift moment",
    defaultPurpose: ["cultivation"],
  },
  {
    key: TEMPLATE_KEYS.THANK_YOU,
    label: "Thank You",
    description: "Gratitude for a recent gift",
    defaultPurpose: ["gratitude"],
  },
];

export const DEFAULT_EMAIL_TEMPLATES = {
  [TEMPLATE_KEYS.RENEWAL_ASK]: {
    subject: "It's that time again — your support means the world",
    body:
      "Dear {donorFirstName},\n\n" +
      "A year ago, you made a gift of {lastGiftAmount} to {chapterName}, and I want you to know what a difference that generosity has made. Your belief in our mission is exactly the kind of partnership that keeps this work possible.\n\n" +
      "As we look ahead to the year to come, I'd love to invite you to renew your support at the same level — or, if the timing is right, to consider stepping up. Every renewal tells us the work matters, and your continued commitment would mean a great deal.\n\n" +
      "If you have a few minutes, I'd welcome the chance to share what's on the horizon. Otherwise, you can renew directly whenever it's convenient.\n\n" +
      "With gratitude,\n" +
      "{signatureName}\n" +
      "{signatureTitle}",
  },

  [TEMPLATE_KEYS.LYBNT]: {
    subject: "We've missed hearing from you",
    body:
      "Dear {donorFirstName},\n\n" +
      "I was looking back at your giving history and noticed that your last gift of {lastGiftAmount} came in on {lastGiftDate} — and I realized it's been a little while since we've connected.\n\n" +
      "I don't want to assume anything about why. Life is busy, priorities shift, and I know mail from organizations can pile up fast. But your past support meant something real, and I didn't want another month to go by without reaching out personally.\n\n" +
      "Would you be open to a short conversation? I'd love to hear what's on your mind, share what we've been working on, and — if the moment feels right — invite you back. No pressure at all.\n\n" +
      "Warmly,\n" +
      "{signatureName}\n" +
      "{signatureTitle}",
  },

  [TEMPLATE_KEYS.SYBNT]: {
    subject: "A note from {chapterName} — thinking of you",
    body:
      "Dear {donorFirstName},\n\n" +
      "Your name came up recently as I was reviewing our donor family, and I wanted to reach out. You've supported {chapterName} in the past — your gift of {lastGiftAmount} in {lastGiftDate} is still a part of the story of what we've been able to build — and I've always appreciated that.\n\n" +
      "I know it's been a stretch since we last heard from you, and I'd love to change that. Would you be open to a quick call or coffee? I'd like to share where things stand, what the coming year looks like, and hear what matters to you right now.\n\n" +
      "Either way, please know your past partnership made a difference. I hope our paths cross again soon.\n\n" +
      "With appreciation,\n" +
      "{signatureName}\n" +
      "{signatureTitle}",
  },

  [TEMPLATE_KEYS.SECOND_GIFT]: {
    subject: "Your gift made an impact — here's what happened next",
    body:
      "Dear {donorFirstName},\n\n" +
      "A little while back, on {lastGiftDate}, you made a first gift of {lastGiftAmount} to {chapterName}. I want you to know that gift didn't just arrive and disappear into a spreadsheet — it helped us do real work, and I've been meaning to tell you about it personally.\n\n" +
      "Research in our field consistently shows that a donor's second gift is the most important one. It's the moment a supporter decides this isn't just a one-time thing — it's a relationship. And because your first gift mattered so much, I'd love to invite you into that relationship.\n\n" +
      "No pressure on amount or timing. But if you're willing, I'd be honored to hear from you — either with a quick reply, a short call, or, when the moment feels right, a second gift that tells us you're with us for the long haul.\n\n" +
      "With real gratitude,\n" +
      "{signatureName}\n" +
      "{signatureTitle}",
  },

  [TEMPLATE_KEYS.THANK_YOU]: {
    subject: "Thank you — your gift just arrived",
    body:
      "Dear {donorFirstName},\n\n" +
      "I wanted to send you a personal note the moment your gift of {lastGiftAmount} came in. Thank you. Truly.\n\n" +
      "A formal acknowledgement will follow, but I didn't want that to be the first thing you heard from us. Your generosity moves the work forward in a very real way, and people like you are the reason {chapterName} can do what it does.\n\n" +
      "If there's anything you'd like to hear more about — where the funds go, what's coming up, how you can be involved beyond giving — just reply to this note. I'd love to stay in touch.\n\n" +
      "With gratitude,\n" +
      "{signatureName}\n" +
      "{signatureTitle}",
  },
};

export const getTemplateDefinition = (key) =>
  EMAIL_TEMPLATE_DEFINITIONS.find((t) => t.key === key) || null;

export const mergeTemplates = (overrides = {}) => {
  const merged = {};
  EMAIL_TEMPLATE_DEFINITIONS.forEach((def) => {
    const override = overrides[def.key] || {};
    merged[def.key] = {
      subject: override.subject || DEFAULT_EMAIL_TEMPLATES[def.key].subject,
      body: override.body || DEFAULT_EMAIL_TEMPLATES[def.key].body,
      isCustomized: Boolean(override.subject || override.body),
    };
  });
  return merged;
};
